/**
 * Sealed envelopes — age (age-encryption.org/v1) sealing plus signed key
 * statements, for the Envelopes app and any consumer that needs
 * E2E-encrypted one-shot envelopes over dumb storage.
 *
 * Design: projects/docs/2026/08/19/end-to-end-encrypted-envelopes-over-public-channels.md
 * App:    projects/docs/2026/08/21/envelopes-app-sealed-mail-over-any-webdav-store.md
 *
 * Key custody: the sealing identity is a NON-EXTRACTABLE WebCrypto X25519
 * CryptoKey persisted by structured clone (IndexedDB) — the private key
 * never exists in stealable form. The claim handle (the statement signer,
 * a handle's continuity root) is stored as an extractable JWK, matching
 * the storage posture of acequia's device/user keys.
 *
 * Statements bind a user-chosen handle to a sealing key. A handle path
 * does not self-certify, so verification recomputes the fingerprint from
 * the embedded recipient and callers TOFU-pin the claim key + fp per
 * (store, handle) — a changed claim key is an alarm, never a silent
 * rotation.
 */

import { jose, age, localforage } from '../deps.js'

const STATEMENT_TYP = 'acequia-key-statement+jws'
// The only signature algorithms a statement may carry. The claim key is
// embedded in the statement and trust comes from the pin, so this is not
// what stops a forgery — it stops a foreign alg/key type (a symmetric `oct`
// JWK under HS256, say) from ever reaching jwtVerify.
const STATEMENT_ALGS = ['EdDSA', 'PS256']

// ---------------------------------------------------------------- storage

let _store = null
function getStore() {
    if (_store) return _store
    if (typeof indexedDB !== 'undefined' && localforage?.createInstance) {
        _store = localforage.createInstance({ name: 'acequia-envelopes' })
    } else {
        // Node/Deno tests: volatile in-memory fallback
        const m = new Map()
        _store = {
            getItem: async (k) => (m.has(k) ? m.get(k) : null),
            setItem: async (k, v) => (m.set(k, v), v),
            removeItem: async (k) => void m.delete(k),
        }
    }
    return _store
}

/** Override the backing store (tests). */
export function configureStore(storeInstance) {
    _store = storeInstance
}

// ---------------------------------------------------------------- helpers

async function sha256Hex(input) {
    const bytes =
        typeof input === 'string' ? new TextEncoder().encode(input) : input
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
}

/** Fingerprint of a sealing key = SHA-256 hex of its recipient string. */
export async function fingerprint(recipient) {
    return sha256Hex(recipient)
}

// ------------------------------------------------------- sealing identity

/**
 * The device's sealing identity: a non-extractable X25519 CryptoKey plus
 * its derived age recipient and fingerprint. Created on first call.
 * @returns {Promise<{privateKey: CryptoKey, recipient: string, fp: string,
 *   createdAt: number}|null>}
 */
export async function getSealIdentity({ create = true } = {}) {
    const store = getStore()
    let identity = await store.getItem('sealIdentity')
    if (!identity && create) {
        const keyPair = await crypto.subtle.generateKey(
            { name: 'X25519' },
            false, // non-extractable — custody parity with signing keys
            ['deriveBits']
        )
        const recipient = await age.identityToRecipient(keyPair.privateKey)
        identity = {
            privateKey: keyPair.privateKey,
            recipient,
            fp: await sha256Hex(recipient),
            createdAt: Date.now(),
        }
        await store.setItem('sealIdentity', identity)
    }
    return identity || null
}

// ------------------------------------------------------------ claim keys

/**
 * The claim handle: the keypair that signs this principal's key
 * statements and is the continuity root of every handle it claims.
 * Ed25519 where the platform supports it, PS256 otherwise.
 * Stored as extractable JWKs (the acequia device/user-key posture).
 */
export async function getClaimKeys({ create = true } = {}) {
    const store = getStore()
    let stored = await store.getItem('claimKeys')
    if (!stored && create) {
        let alg = 'EdDSA'
        let keyPair
        try {
            keyPair = await jose.generateKeyPair(alg, {
                crv: 'Ed25519',
                extractable: true,
            })
        } catch {
            alg = 'PS256'
            keyPair = await jose.generateKeyPair(alg, { extractable: true })
        }
        stored = {
            alg,
            publicJwk: await jose.exportJWK(keyPair.publicKey),
            privateJwk: await jose.exportJWK(keyPair.privateKey),
            createdAt: Date.now(),
        }
        await store.setItem('claimKeys', stored)
    }
    return stored || null
}

/** Thumbprint of this device's claim key (pin/ownership comparisons). */
export async function claimThumbprint() {
    const claim = await getClaimKeys()
    return jose.calculateJwkThumbprint(claim.publicJwk)
}

// ------------------------------------------------------------ statements

/**
 * Mint a signed key statement binding `handle` to this device's sealing
 * key, signed by the claim handle.
 * @returns {Promise<string>} compact JWS
 */
export async function createKeyStatement({ handle, notAfter } = {}) {
    if (!handle) throw new Error('handle required')
    const identity = await getSealIdentity()
    const claim = await getClaimKeys()
    const signingKey = await jose.importJWK(claim.privateJwk, claim.alg)
    const payload = {
        sub: handle,
        kid: `seal-${identity.fp.slice(0, 16)}`,
        use: 'seal',
        alg: 'age-x25519',
        pk: identity.recipient,
        fp: identity.fp,
        claim: claim.publicJwk,
        ...(notAfter ? { notAfter } : {}),
    }
    return new jose.SignJWT(payload)
        .setProtectedHeader({ alg: claim.alg, typ: STATEMENT_TYP })
        .setIssuedAt()
        .sign(signingKey)
}

/**
 * Mint a signed key statement for an EXTERNAL recipient — sponsored
 * registration. The sealing key belongs to someone else (e.g. a shell user's
 * `age-keygen` identity); this device only vouches by signing with its claim
 * handle, which thereby becomes that handle's continuity root. Senders' TOFU
 * pins bind to the sponsor's vouching, NOT to a verification of identity —
 * state that plainly wherever this is offered.
 *
 * @param {{handle: string, recipient: string, notAfter?: number}} params
 * @returns {Promise<string>} compact JWS
 */
export async function createKeyStatementFor({ handle, recipient, notAfter } = {}) {
    if (!handle) throw new Error('handle required')
    if (!recipient?.startsWith('age1'))
        throw new Error('recipient must be an age1… string')
    const claim = await getClaimKeys()
    const signingKey = await jose.importJWK(claim.privateJwk, claim.alg)
    const fp = await sha256Hex(recipient)
    const payload = {
        sub: handle,
        kid: `seal-${fp.slice(0, 16)}`,
        use: 'seal',
        alg: 'age-x25519',
        pk: recipient,
        fp,
        claim: claim.publicJwk,
        sponsored: true, // the sealing key is not this device's
        ...(notAfter ? { notAfter } : {}),
    }
    return new jose.SignJWT(payload)
        .setProtectedHeader({ alg: claim.alg, typ: STATEMENT_TYP })
        .setIssuedAt()
        .sign(signingKey)
}

/**
 * Verify a key statement: signature against the embedded claim key,
 * structural claims, and the fingerprint RECOMPUTED from the embedded
 * recipient (never trusted as stated). Statement paths don't
 * self-certify — pair this with checkAndPin().
 * @returns {Promise<{payload: object, claimJwk: object,
 *   claimThumbprint: string}>}
 */
export async function verifyKeyStatement(jws, { expectedHandle } = {}) {
    const header = jose.decodeProtectedHeader(jws)
    if (header.typ !== STATEMENT_TYP)
        throw new Error(`not a key statement (typ ${header.typ})`)
    if (!STATEMENT_ALGS.includes(header.alg))
        throw new Error(`statement alg ${header.alg} is not allowed`)
    const unverified = jose.decodeJwt(jws)
    if (!unverified.claim) throw new Error('statement has no claim key')
    if (unverified.claim.kty !== 'OKP' && unverified.claim.kty !== 'RSA')
        throw new Error(`claim key type ${unverified.claim.kty} is not allowed`)
    const claimKey = await jose.importJWK(unverified.claim, header.alg)
    const { payload } = await jose.jwtVerify(jws, claimKey, {
        algorithms: STATEMENT_ALGS,
    })
    if (payload.use !== 'seal' || payload.alg !== 'age-x25519')
        throw new Error('not an age-x25519 sealing statement')
    if (!payload.pk?.startsWith('age1'))
        throw new Error('statement pk is not an age recipient')
    const fp = await sha256Hex(payload.pk)
    if (payload.fp !== fp)
        throw new Error('statement fp does not match its recipient')
    if (expectedHandle && payload.sub !== expectedHandle)
        throw new Error(
            `statement is for "${payload.sub}", not "${expectedHandle}"`
        )
    if (payload.notAfter && Date.now() / 1000 > payload.notAfter)
        throw new Error('statement expired (notAfter)')
    return {
        payload,
        claimJwk: payload.claim,
        claimThumbprint: await jose.calculateJwkThumbprint(payload.claim),
    }
}

// ------------------------------------------------------------------ pins
//
// TOFU pins per (storeKey, handle): first contact records the claim-key
// thumbprint + fp; later contacts compare. A mismatch is returned, never
// auto-repinned — resetPin() is the explicit user override.

const pinKey = (storeKey, handle) => `pin:${storeKey}:${handle}`

export async function getPin(storeKey, handle) {
    return (await getStore().getItem(pinKey(storeKey, handle))) || null
}

/**
 * Compare a verified statement against the pin for (storeKey, handle),
 * pinning on first contact.
 * @returns {Promise<{status: 'first-contact'|'match'|'mismatch', pin}>}
 */
export async function checkAndPin({ storeKey, handle, verified }) {
    const store = getStore()
    const key = pinKey(storeKey, handle)
    const existing = await store.getItem(key)
    const seen = {
        claimThumbprint: verified.claimThumbprint,
        fp: verified.payload.fp,
    }
    if (!existing) {
        const pin = { ...seen, firstSeen: Date.now(), lastSeen: Date.now() }
        await store.setItem(key, pin)
        return { status: 'first-contact', pin }
    }
    if (
        existing.claimThumbprint === seen.claimThumbprint &&
        existing.fp === seen.fp
    ) {
        existing.lastSeen = Date.now()
        await store.setItem(key, existing)
        return { status: 'match', pin: existing }
    }
    return { status: 'mismatch', pin: existing, seen }
}

/** Explicit user override after out-of-band verification. */
export async function resetPin(storeKey, handle) {
    await getStore().removeItem(pinKey(storeKey, handle))
}

// ------------------------------------------------------------- envelopes

/**
 * Seal data to one or more recipients. @returns {Promise<Uint8Array>}
 */
export async function seal(recipients, data) {
    const enc = new age.Encrypter()
    for (const r of Array.isArray(recipients) ? recipients : [recipients])
        enc.addRecipient(r)
    return enc.encrypt(data)
}

/**
 * Open an envelope with this device's sealing identity.
 * @returns {Promise<string|Uint8Array>}
 */
export async function open(ciphertext, { as = 'text' } = {}) {
    const identity = await getSealIdentity({ create: false })
    if (!identity) throw new Error('no sealing identity on this device')
    const dec = new age.Decrypter()
    dec.addIdentity(identity.privateKey)
    return dec.decrypt(ciphertext, as)
}

/** Content-hash envelope filename: idempotent send, retry = no-op. */
export async function envelopeName(ciphertext) {
    return `${await sha256Hex(ciphertext)}.age`
}

export { age }
